import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useMoveJob } from '../../../data/truckingData';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import MasterBolSignPanel, { MasterBolSignedNote, type MasterBolForSign } from '../../../components/trucking/MasterBolSignPanel';

/**
 * A driver on a rig move gets the consultant's signature on the master BOL from
 * the phone (api#941): drawn on the screen, or recorded as signed on paper.
 *
 * Reached from the rig-move banner on the driver's load (DriverLoadView). GET
 * /trucking/move-jobs/:id is open to the driver; the signature upload is what
 * the API restricts, to a driver named on one of this move job's loads, and its
 * refusal is shown by the upload control as sent.
 *
 * The route requires the master BOL to be `complete` (every load signed) and
 * moves it to `finalized`. Before that there is nothing to sign, and the screen
 * says which state it is in rather than offering a button that 400s.
 */
type MasterBol = MasterBolForSign & {
  master_bol_number?: string | null;
  total_loads?: number | null;
  loads_signed?: number | null;
};

export default function DriverMasterBolScreen() {
  const { moveJobId = '' } = useParams<{ moveJobId: string }>();
  const navigate = useNavigate();
  const query = useMoveJob(moveJobId);
  const failure = queryLoadFailure(query);
  const job = (failure ? null : query.data ?? null) as (Record<string, unknown> & { masterBol?: MasterBol | null }) | null;
  const masterBol = job?.masterBol ?? null;
  const name = typeof job?.name === 'string' && job.name ? job.name : '—';
  const progress =
    masterBol && typeof masterBol.loads_signed === 'number' && typeof masterBol.total_loads === 'number'
      ? `${masterBol.loads_signed} of ${masterBol.total_loads} loads signed`
      : '—';

  let body: React.ReactNode;
  if (failure) {
    body = <LoadFailureNotice error={queryFailureError(query)} subject="this rig move" />;
  } else if (query.isLoading) {
    body = (
      <div className="flex justify-center py-12" aria-hidden="true">
        <Loader2 className="h-6 w-6 animate-spin text-fieldiq-muted" />
      </div>
    );
  } else if (!job) {
    body = <p className="py-10 text-center text-sm text-fieldiq-muted">Rig move not found</p>;
  } else if (!masterBol) {
    body = <p data-testid="master-bol-state" className="text-sm text-fieldiq-muted">The office has not made the master BOL yet.</p>;
  } else if (masterBol.status === 'finalized') {
    body = <MasterBolSignedNote masterBol={masterBol} />;
  } else if (masterBol.status === 'complete') {
    body = <MasterBolSignPanel moveJobId={moveJobId} masterBol={masterBol} />;
  } else {
    body = <p data-testid="master-bol-state" className="text-sm text-fieldiq-muted">Signing opens once every load is signed.</p>;
  }

  return (
    <div data-testid="driver-master-bol-page" className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div
        className="sticky top-0 z-30 border-b border-fieldiq-border bg-fieldiq-dark px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="mx-auto flex max-w-md items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} aria-label="Back" className="text-fieldiq-muted">
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-white">Master BOL{masterBol?.master_bol_number ? ` ${masterBol.master_bol_number}` : ''}</h1>
            <p className="truncate text-xs text-fieldiq-muted">Rig move: {name}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 px-4 py-4">
        {masterBol && (
          <p data-testid="master-bol-progress" className="text-sm text-fieldiq-text">{progress}</p>
        )}
        <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">{body}</div>
      </div>

    </div>
  );
}
